// ════════════════════════════════════════════════════════════════════════════
//  Inutilização de números AVULSOS da NF-e — os que ficaram sem nota (salto na
//  numeração, nota descartada). É irreversível: o diálogo mostra o ambiente em que
//  a faixa vai sair e pede a justificativa que a SEFAZ exige (mínimo 15 caracteres).
//  Número que ainda tem nota inutiliza-se pela própria nota.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { notaFiscalService } from '@/features/fiscal/services/notaFiscalService';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface Props {
  open: boolean;
  onFechar: () => void;
  /** Chamado depois que a SEFAZ homologa a inutilização (a lista de notas ganha as linhas novas). */
  onInutilizado: () => void;
}

export function InutilizarFaixaDialog({ open, onFechar, onInutilizado }: Props) {
  const { data: contexto, isLoading } = useQuery({
    queryKey: ['notas-fiscais', 'inutilizacao-contexto'],
    queryFn: notaFiscalService.contextoInutilizacao,
    enabled: open,
    staleTime: 0, // o ambiente do emitente pode ter mudado desde a última abertura
  });

  const [serie, setSerie]                 = useState('1');
  const [inicial, setInicial]             = useState('');
  const [final, setFinal]                 = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [enviando, setEnviando]           = useState(false);

  useEffect(() => {
    if (!open) return;
    setInicial(''); setFinal(''); setJustificativa('');
  }, [open]);
  useEffect(() => { if (contexto) setSerie(String(contexto.serie)); }, [contexto]);

  const nIni = Number(inicial), nFim = Number(final || inicial);
  const maximo = contexto?.maximoPorFaixa ?? 100;
  const minimo = contexto?.numeroMinimo ?? 1;
  const fmtNumero = (n: number) => n.toLocaleString('pt-BR');
  const quantidade = nIni > 0 && nFim >= nIni ? nFim - nIni + 1 : 0;
  const erro =
      !inicial                    ? null
    : !(nIni > 0)                 ? 'Informe o número inicial.'
    : nIni < minimo               ? `Em homologação só é possível inutilizar a partir do número ${fmtNumero(minimo)} (faixa de teste).`
    : nFim < nIni                 ? 'O número final não pode ser menor que o inicial.'
    : quantidade > maximo         ? `A faixa tem ${quantidade} números; o limite é de ${maximo} por vez.`
    : null;
  const podeEnviar = !!contexto?.disponivel && quantidade > 0 && !erro
    && justificativa.trim().length >= 15 && Number(serie) >= 0 && !enviando;

  const enviar = async () => {
    if (!contexto) return;
    setEnviando(true);
    try {
      const r = await notaFiscalService.inutilizarFaixa({
        serie: Number(serie), numeroInicial: nIni, numeroFinal: nFim,
        justificativa: justificativa.trim(), producao: contexto.producao,
      });
      if (r.sucesso) {
        toast.success(`Faixa inutilizada na SEFAZ. Protocolo ${r.protocolo ?? '—'}.`);
        onInutilizado();
        onFechar();
      } else {
        toast.error(`A SEFAZ não inutilizou a faixa: ${r.cStat} — ${r.xMotivo}`, { duration: 12_000 });
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err), { duration: 12_000 });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !enviando) onFechar(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Inutilizar faixa de números</DialogTitle>
        </DialogHeader>

        {isLoading || !contexto ? (
          <p className="text-sm text-muted-foreground py-4">Carregando…</p>
        ) : !contexto.disponivel ? (
          <p className="text-sm text-destructive py-4">{contexto.motivo ?? 'A inutilização não está disponível.'}</p>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Para números que ficaram <strong>sem nota</strong> (salto na numeração, nota descartada). Os números
              inutilizados nunca mais poderão ser usados. Número de nota rejeitada inutiliza-se pela própria nota.
            </p>
            <div className={`rounded-md border p-3 text-sm ${contexto.producao
              ? 'border-red-300 bg-red-50 text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-100'
              : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-100'}`}>
              Ambiente do emitente: <strong>{contexto.producao ? 'PRODUÇÃO' : 'Homologação (sem valor fiscal)'}</strong>.
              {' '}A faixa será inutilizada nesse ambiente.
              {minimo > 1 && (
                <> Em homologação só entram números da faixa de teste, a partir de <strong>{fmtNumero(minimo)}</strong>:
                números menores são da numeração de produção.</>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label htmlFor="inut-serie">Série</Label>
                <Input id="inut-serie" type="number" min={0} max={999} value={serie}
                  onChange={(e) => setSerie(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inut-inicial">Número inicial *</Label>
                <Input id="inut-inicial" type="number" min={1} value={inicial}
                  onChange={(e) => setInicial(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="inut-final">Número final</Label>
                <Input id="inut-final" type="number" min={1} value={final} placeholder="igual ao inicial"
                  onChange={(e) => setFinal(e.target.value)} />
              </div>
            </div>
            {erro
              ? <p className="text-xs text-destructive">{erro}</p>
              : quantidade > 0 && (
                <p className="text-xs text-muted-foreground">
                  {quantidade === 1 ? `1 número: ${nIni}.` : `${quantidade} números: de ${nIni} a ${nFim}.`} Limite de {maximo} por vez.
                </p>
              )}

            <div className="space-y-1">
              <Label htmlFor="inut-just">Justificativa * (mínimo 15 caracteres)</Label>
              <Textarea id="inut-just" rows={3} maxLength={255} value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
                placeholder="Ex.: números não utilizados por salto na sequência na troca de sistema" />
              <p className="text-xs text-muted-foreground">{justificativa.trim().length}/255</p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onFechar} disabled={enviando}>Voltar</Button>
          <Button variant="destructive" onClick={enviar} disabled={!podeEnviar}>
            {enviando ? 'Enviando à SEFAZ…'
              : quantidade > 1 ? `Inutilizar ${quantidade} números` : 'Inutilizar o número'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
